import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function ExpenditureForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    amount: '',
    created_at: new Date().toISOString().split('T')[0],
    purpose: '',
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchExpenditure();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/finance/expenditure-categories');
      setCategories(response.data.data || []);
    } catch (err) {
      setError('Failed to fetch categories');
    }
  };

  const fetchExpenditure = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/finance/expenditures/${id}`);
      const expenditure = response.data.data;
      setFormData({
        name: expenditure.name || '',
        category_id: expenditure.category_id || '',
        amount: expenditure.amount || '',
        created_at: expenditure.created_at ? expenditure.created_at.split('T')[0] : new Date().toISOString().split('T')[0],
        purpose: expenditure.purpose || '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch expenditure');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        category_id: parseInt(formData.category_id),
      };

      if (id) {
        await api.put(`/finance/expenditures/${id}`, data);
      } else {
        await api.post('/finance/expenditures', data);
      }
      navigate('/dashboard/expenditure');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save expenditure');
    } finally {
      setLoading(false);
    }
  };

  if (loading && id) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>{id ? 'Expenditure Detail' : 'New Expenditure'}</Box>
          </>
        }
        title={id ? 'Expenditure Detail' : 'New Expenditure'}
        actions={
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/expenditure')}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.ink }}
          >
            Expenditures
          </Button>
        }
      />

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Category*</InputLabel>
                <Select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleChange}
                  label="Category*"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="amount"
                name="amount"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={formData.amount}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Created At"
                name="created_at"
                type="date"
                value={formData.created_at}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.name || !formData.category_id || !formData.amount}
            >
              {loading ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/expenditure')}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
